import type { Pontos } from './escala'
import { DADOS_GDM_TESTE } from './gdm-teste-dados'
import {
  BONUS_DA_BASE,
  BONUS_DO_TRAIT,
  pontosDoModelo,
  raridadeDoTrait,
  type AlvoDeTrait,
  type ExtraDePontos,
} from './parceira/modelos'
import type { PerfilDeTaco, RaridadeCunhavel } from './ruleset'

/**
 * Os pontos dos tacos de TESTE da Gangue do Macaco — um modelo DIFERENTE do
 * ruleset, de propósito, e só para eles.
 *
 * SEM CLASSE de taco (padrão, pesado, efeito, controle). No ruleset cada
 * raridade tem um orçamento TOTAL e a classe decide onde gastá-lo, e ninguém
 * domina ninguém. Aqui:
 *
 * - O PISO é `PISO_GDM` (50) em cada atributo, ou o grátis onde ele for maior:
 *   todo taco da Gangue começa em 50 em tudo e nunca fica abaixo do grátis. A
 *   Gangue é uma das coleções principais do jogo, e com o piso no grátis
 *   (46/36/46/0 do ruleset, que o Marcos manteve no #27) os tacos de traits
 *   comuns saíam quase iguais a ele — e sem quebra nenhuma.
 * - CADA TRAIT ALIMENTA UM ATRIBUTO, com mais pontos quanto mais raro:
 *   Roupa → peso, Estilo → efeito, Olhos → aderência, Rosto → quebra. O
 *   Macaco (a cor) é a base do bicho e dá um pouco em todos.
 * - O ESPECIAL (1/1) nasce FULL: 100 em tudo.
 *
 * Qualquer taco da coleção é melhor que o gratuito — o que o ruleset proíbe,
 * e por isso este modelo não sai de `GDM-TESTE` sem decisão do dono do jogo.
 * O teto é o da física (`CUE_LIMITS`): nem o full passa dele.
 *
 * O Rosto na QUEBRA não é acaso: é o trait mais concentrado da coleção
 * (Alegre em 57%), então a maioria fica perto do piso na quebra — o
 * atributo que mais pesa na partida, porque decide a abertura.
 *
 * A CONTA não mora mais aqui. Ela virou o modelo `por-traits` de
 * `parceira/modelos.ts`: a MESMA fórmula, generalizada para qualquer coleção
 * parceira (o alvo de cada categoria vem das regras da coleção em vez de ser
 * fixo). Sobrou aqui o que só a GDM tem: a tabela trait → atributo e os mints
 * de teste. Duas cópias da fórmula do log poderiam divergir num rebalanceio —
 * e aí o mesmo NFT valeria coisas diferentes conforme a porta por onde
 * entrasse.
 */

export type TraitGdm = 'Macaco' | 'Rosto' | 'Roupa' | 'Olhos' | 'Estilo'

/**
 * Um taco de teste da GDM: o número do NFT, a raridade (borda e orçamento de
 * níveis), se é 1/1, e a frequência de cada trait que ele TEM (0 a 1).
 */
export type PerfilGdm = {
  numero: number
  raridade: RaridadeCunhavel
  especial: boolean
  traits: Partial<Record<TraitGdm, number>>
}

export const COLECAO_GDM_TESTE = 'GDM-TESTE'

/** Mint inventado, com o `X` separando o número do enchimento: `1X111…` e `11X11…` não colidem. */
export const mintGdmDeTeste = (numero: number): string => `GDMTESTE${numero}X`.padEnd(44, '1')

/** O número do NFT de um mint de teste da GDM, ou nulo se não for um. */
export function numeroDoMintGdm(mint: string): number | null {
  const m = /^GDMTESTE(\d+)X/.exec(mint)
  return m ? Number(m[1]) : null
}

/**
 * Qual alvo cada trait da GDM alimenta — o mesmo papel que o
 * `atributoDaCategoria` das regras de uma coleção parceira, aqui fixo porque
 * a GDM de teste não passa pelo cadastro do painel.
 *
 * O Macaco (a cor do bicho) vai em `todos`: é a base que sobe um pouco em tudo.
 */
export const ALVO_DO_TRAIT: Record<TraitGdm, AlvoDeTrait> = {
  Macaco: 'todos',
  Roupa: 'peso',
  Estilo: 'efeito',
  Olhos: 'aderencia',
  Rosto: 'quebra',
}

/**
 * Os pesos e a curva são os do modelo geral, reexportados com os nomes que a
 * GDM usa: UM valor só, não dois que podem divergir.
 */
export { BONUS_DO_TRAIT, raridadeDoTrait }
/** O máximo que a cor do macaco soma em CADA atributo (a `base` do modelo). */
export const BONUS_DO_MACACO = BONUS_DA_BASE

/**
 * Onde todo taco da Gangue começa, em CADA atributo (o grátis não é tocado).
 *
 * É o `piso` das regras de uma coleção parceira, aqui fixo porque a GDM de
 * teste não passa pelo cadastro do painel. O modelo nunca desce abaixo do
 * grátis, então isto só sobe.
 */
export const PISO_GDM = 50
const PISO_DA_GDM: Pontos = { peso: PISO_GDM, efeito: PISO_GDM, aderencia: PISO_GDM, quebra: PISO_GDM }

/**
 * O perfil que o modelo recebe. O `por-traits` NÃO olha raridade nem
 * arquétipo (os pontos saem dos traits), mas a assinatura pede um perfil;
 * vai o equivalente ao da linha de uma parceira, com a seed do número.
 */
export const perfilGdm = (p: PerfilGdm): PerfilDeTaco => ({
  raridade: p.raridade,
  arquetipo: 'padrao',
  seed: p.numero + 1,
  geracao: 0,
})

export function pontosGdm(p: PerfilGdm): Pontos {
  const traits: Record<string, number> = {}
  for (const [trait, alvo] of Object.entries(ALVO_DO_TRAIT) as [TraitGdm, AlvoDeTrait][]) {
    const f = p.traits[trait]
    // Vence a MAIS RARA, como no `montarTabela` das coleções parceiras. Hoje
    // `ALVO_DO_TRAIT` não repete alvo, então nunca empata; a regra está aqui
    // para as duas portas não poderem divergir se um dia repetir.
    if (f !== undefined && (traits[alvo] === undefined || f < traits[alvo]!)) traits[alvo] = f
  }
  const extra: ExtraDePontos = { traits }
  if (p.especial) extra.especial = true
  return pontosDoModelo('por-traits', perfilGdm(p), extra, PISO_DA_GDM)
}

/** Os pontos de um mint de teste da GDM, ou nulo se o mint não for um dos tacos de teste. */
export function pontosGdmDoMint(mint: string): Pontos | null {
  const numero = numeroDoMintGdm(mint)
  const p = numero === null ? undefined : DADOS_GDM_TESTE.find((d) => d.numero === numero)
  return p ? pontosGdm(p) : null
}
